import React from 'react';
import {
  Box,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BirdzTitle } from '@applications-terrains/birdz-react-library';

type RejectionModalProps = {
  open: boolean;
  invalidData: any[];
  onClose: () => void;
};

// Les clés d'erreur renvoyées par l'API sont des noms de champs techniques : on les affiche
// sous le nom de la colonne du fichier d'import, que l'utilisateur doit corriger.
const TRANSLATED_REJECTION_FIELDS = new Set([
  'equipment',
  'equipment_family_type',
  'contract',
  'city',
  'address_city',
  'address_zip_code',
  'address_insee_code',
  'config_name',
  'configuration',
  'intervention_type_code',
  'target_tour',
  'target_dates_constrains_type',
  'target_datetime_1',
  'target_datetime_2',
  'service_point_customer_id',
  'contact_name',
  'contact_telephone_raw'
]);

// Erreurs portant sur la ligne entière : elles ne visent aucune colonne à corriger.
const FIELDLESS_ERROR_KEYS = new Set(['non_field_errors', 'resolution']);

// Comparaison tolérante aux accents et à la casse, pour repérer un libellé déjà présent
// dans le message.
const normalize = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Un identifiant de capteur n'est garanti sur aucun des deux imports : la colonne
// « Equipement » n'existe qu'en maintenance, et les champs équivalents de la création sont
// tous facultatifs. Même ordre de repli que le back (validate_equipment_ownership).
const getEquipmentLabel = (data: Record<string, unknown>): string => {
  const identifier =
    data.equipment ?? data.equipment_id ?? data.service_point_customer_id ?? data.device_id;
  return identifier ? String(identifier) : '';
};

// Le back numérote les lignes de données ; l'utilisateur, lui, lit le numéro de son tableur,
// en-tête comprise — un fichier sans en-tête étant déjà refusé en amont.
const getFileRowNumber = (rowNumber: number | undefined): string =>
  typeof rowNumber === 'number' ? String(rowNumber + 1) : '';

export default function RejectionModal({ open, invalidData, onClose }: RejectionModalProps) {
  const { t } = useTranslation();

  // L'API renvoie tantôt un message seul, tantôt une liste de messages pour un même champ.
  const toMessageList = (messages: string | string[]): string[] =>
    Array.isArray(messages) ? messages : [messages];

  // Une clé non répertoriée reste affichée telle quelle plutôt que de disparaître.
  const getFieldLabel = (field: string): string =>
    TRANSLATED_REJECTION_FIELDS.has(field)
      ? t(`bconnectMaintenanceOrder.pdsSelection.rejectionFields.${field}`)
      : field;

  // Le nom du champ n'est affiché que s'il apporte une information : les messages génériques
  // de l'API ne nomment pas leur champ ("Ce champ est obligatoire."), les autres si.
  const formatError = (field: string, message: string): string => {
    if (FIELDLESS_ERROR_KEYS.has(field)) {
      return message;
    }
    const label = getFieldLabel(field);
    return normalize(message).includes(normalize(label)) ? message : `${label}: ${message}`;
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 3,
          // Largeur bornée plutôt qu'un plancher : deux colonnes courtes n'ont pas besoin
          // de 600px, et `minWidth` débordait de l'écran sur une fenêtre étroite.
          width: 'min(520px, calc(100vw - 32px))',
          borderRadius: 1
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <BirdzTitle>
            {t('bconnectMaintenanceOrder.pdsSelection.pdsListing.listOfRejections')}
          </BirdzTitle>
        </Box>
        <Box
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { WebkitAppearance: 'none', width: '6px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.5)', borderRadius: '3px' }
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('bconnectMaintenanceOrder.pdsSelection.pdsListing.rowNumber')}&nbsp;*
                </TableCell>
                <TableCell>
                  {t('bconnectMaintenanceOrder.pdsSelection.pdsListing.equipment')}
                </TableCell>
                <TableCell>
                  {t('bconnectMaintenanceOrder.pdsSelection.pdsListing.reasonForRejection')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invalidData.map((item: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{getFileRowNumber(item._row_number)}</TableCell>
                  <TableCell>{getEquipmentLabel(item.data ?? {})}</TableCell>
                  <TableCell>
                    <ul>
                      {Object.entries(item.errors as Record<string, string | string[]>).flatMap(
                        ([field, messages]) =>
                          toMessageList(messages).map((message, index) => (
                            <li key={`${field}-${index}`}>{formatError(field, message)}</li>
                          ))
                      )}
                    </ul>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          {t('bconnectMaintenanceOrder.pdsSelection.pdsListing.rowNumberHint')}
        </Typography>
        <Button onClick={onClose} sx={{ display: 'block', margin: '16px auto 0' }}>
          {t('bconnectMaintenanceOrder.pdsSelection.pdsListing.close')}
        </Button>
      </Box>
    </Modal>
  );
}
